import type { MessagingMessage } from "@/model/messaging";
import { sendSSEGetRequest, type SSEMessageDelta } from "@/model/sse";
import { defer, finalize, map, mergeMap, Observable, takeWhile } from "rxjs";

export interface SseOptions {
  taskUid: string;
  realm?: string;
  baseUrl?: string;
}

export interface SseResult {
  message: string;
  hasData: boolean;
  finished: boolean;
}

export class Sse {
  get taskUid() {
    return this.options.taskUid;
  }
  messageText = "";
  message?: MessagingMessage;

  #isRunning = false;
  #hasData = false;
  constructor(private readonly options: SseOptions) {}

  start() {
    const { taskUid, baseUrl, realm } = this.options;

    return defer(async () => {
      if (this.#isRunning) {
        throw new Error("Already running");
      }
      this.#isRunning = true;
      this.#hasData = false;

      const url = getSSERequestUrl({
        baseUrl,
        taskUid,
        realm: realm ?? "human_agent",
      });

      const resp = await sendSSEGetRequest<SSEMessageDelta>({ url });
      return resp;
    }).pipe(
      mergeMap((resp) => {
        return new Observable<SSEMessageDelta | undefined>((subscriber) => {
          let isCanceled = false;
          async function run() {
            for (;;) {
              const delta = await resp.next();
              if (isCanceled) {
                return;
              }
              subscriber.next(delta);
            }
          }
          void run().catch((err: unknown) => {
            subscriber.error(err);
          });

          return () => {
            isCanceled = true;
          };
        });
      }),
      map((delta) => {
        if (delta) {
          this.#handleDelta(delta);
        }

        return {
          delta,
          messageText: this.messageText,
          message: this.message,
          hasData: this.#hasData,
          finished: !delta,
        };
      }),
      takeWhile(({ finished }) => {
        return !finished;
      }),
      finalize(() => {
        this.#isRunning = false;
      })
    );
  }

  #handleDelta(delta: SSEMessageDelta) {
    this.#hasData = true;

    if (delta.type !== "delta") {
      return;
    }

    const agentDelta = delta.data.data;
    if (
      ["graph_agent_output", "datastore_response_delta"].includes(
        delta.data.type
      )
    ) {
      if (agentDelta.type === "agent_message_chunk") {
        this.messageText += agentDelta.text;
      }
    }

    if (["agent_message"].includes(delta.data.type)) {
      if (agentDelta.type === "agent_message") {
        this.message = agentDelta.message as MessagingMessage;
      }
    }
  }
}

function getSSERequestUrl(args: {
  baseUrl?: string;
  taskUid: string;
  realm: string;
}) {
  const { baseUrl, taskUid, realm } = args;
  return `${baseUrl ?? ""}/v1/tasks/${taskUid}/realm/${realm}/stream_result_sse`;
}
